import { Container, FlexContainer, H4, Modal, TextLink } from "@carrotfertility/carotene"
import React, { useState } from "react"
import styled from "styled-components"

type ModalControlType = {
  isOpen: boolean
}

type LinkModelContextType = {
  addModal: ({ modalId, isOpen }: { modalId: string; isOpen?: boolean }) => void
  openModal: (modalId: string) => void
  closeModal: (modalId: string) => void
  isModalOpen: (modalId: string) => boolean
  isModalRegistered: (modalId: string) => boolean
}

const LinkModalContext = React.createContext(null)
export const useLinkModalContext = (): LinkModelContextType => React.useContext(LinkModalContext)

export function LinkModalContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [modals, setModals] = useState<{ [key: string]: ModalControlType }>({})
  const addModal = ({ modalId, isOpen = false }: { modalId: string; isOpen?: boolean }): void => {
    const newModals = {
      [modalId]: { isOpen }
    }
    const updatedModals = { ...modals, ...newModals }
    setModals(updatedModals)
  }
  const openModal = (modalId: string): void => {
    setModals({ ...modals, ...{ [modalId]: { isOpen: true } } })
  }
  const closeModal = (modalId: string): void => {
    setModals({ ...modals, ...{ [modalId]: { isOpen: false } } })
  }
  const isModalOpen = (modalId: string): boolean => modals[modalId].isOpen

  const isModalRegistered = (modalId: string): boolean => !!modals[modalId]
  return (
    <LinkModalContext.Provider
      value={{
        addModal,
        openModal,
        closeModal,
        isModalOpen,
        isModalRegistered
      }}
    >
      {children}
    </LinkModalContext.Provider>
  )
}

export const LinkToModal = ({ text, toModalId }: { text: string; toModalId: string }): JSX.Element => {
  const { isModalRegistered, openModal } = useLinkModalContext()
  if (isModalRegistered(toModalId)) {
    return (
      <TextLink size="medium" onClick={() => openModal(toModalId)}>
        {text}
      </TextLink>
    )
  }
  return <>{text}</>
}

const ModalContainer = styled(Container)`
  position: relative;
`

const ModalContents = styled(FlexContainer)`
  overflow-y: auto;

  p:last-child {
    margin-bottom: 0;
  }
`

const ContentFade = styled.div`
  position: absolute;
  bottom: 35px;
  left: 36px;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  transform: rotate(-180deg);
  height: 50px;
  width: calc(100% - 80px);
`
export const ModalFromLink = ({
  modalId,
  title,
  children
}: {
  modalId: string
  title?: string
  children: React.ReactNode
}): JSX.Element => {
  const { addModal, isModalRegistered, closeModal, isModalOpen } = useLinkModalContext()
  if (!isModalRegistered(modalId)) {
    addModal({ modalId })
    return null
  }

  const isOpen = isModalOpen(modalId)
  const onClose = (): void => closeModal(modalId)

  return (
    <Modal open={isOpen} onClose={onClose} isDismissable showClose fullScreenMobile>
      {title && <H4 paddingLeftRight={"large"}>{title}</H4>}
      <ModalContainer paddingTopBottom="large">
        <ModalContents
          direction="column"
          padding="0 0 50px 0"
          paddingLeftRight="large"
          maxHeight={["calc(100vh - 150px)", null, null, "60vh"]}
          gap="small"
        >
          {children}
        </ModalContents>
        <ContentFade />
      </ModalContainer>
    </Modal>
  )
}
